import { mat4 } from "gl-matrix";
import { Camera } from "../../../../SharedGL/Camera";
import { IRGBA } from "../../TimelineUtils";
import { SelectedDayShader } from "./SelectedDayShader";

interface IFlightGLAttributes {
  position: WebGLBuffer;
}

export interface IHighlighColors {
  fill: IRGBA;
  outline: IRGBA;
}

export class SelectedDayGL {
  private buffers: IFlightGLAttributes;
  private selectedDayShader: SelectedDayShader;

  private positions: number[];

  constructor(gl: WebGLRenderingContext) {
    this.selectedDayShader = new SelectedDayShader(gl);

    // Here's where we call the routine that builds all the
    // objects we'll be drawing.
    this.buffers = this.initBuffers(gl);
  }

  //
  // initBuffers
  //
  // Initialize the buffers we'll need. For this demo, we just
  // have one object -- a simple two-dimensional square.
  //
  protected initBuffers = (gl: WebGLRenderingContext): IFlightGLAttributes => {
    // Create a buffer for the square's positions.

    const positionBuffer = gl.createBuffer();

    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

    // Now create an array of positions for the square.
    const y0 = 0; // FlightGL.actualDepartureStart + rowStart; // this.aircraftActualFlightHeight;
    const y = 1; // FlightGL.actualDepartureHeight;// FlightGL.actualDepartureHeight; // rowStart + totalHeight -  FlightGL.aircraftRowPadding;
    const x0 = 0; // flight.x0 - Camera.referenceTime;
    const x = 1; // flight.x - Camera.referenceTime;
    this.positions = [x0, y, x0, y0, x, y, x, y0];

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.STATIC_DRAW);

    return {
      position: positionBuffer,
    };
  };

  private drawSelectedDay = (
    gl: WebGLRenderingContext,
    camera: Camera,
    timePos: number[],
    color: IRGBA,
    yRange?: [number, number]
  ): void => {
    this.selectedDayShader.useProgram(gl);
    this.selectedDayShader.bindPositionsBuffer(gl, this.buffers.position);
    this.selectedDayShader.setColorUniform(gl, color);

    const [yStart, yEnd] = yRange ?? camera.getYRange();
    const [xStart, xEnd] = camera.getXRange().map((val: number) => val - Camera.referenceTime);
    const [x0, x] = timePos.map((val: number) => val - Camera.referenceTime);
    const height = yEnd - yStart;
    const width = x0 - xStart;
    const endWidth = xEnd - x;

    const modelViewMatrix = mat4.create();
    mat4.translate(
      modelViewMatrix, // destination matrix
      modelViewMatrix, // matrix to translate
      [xStart, yStart, -1.0]
    ); // amount to translate
    mat4.scale(modelViewMatrix, modelViewMatrix, [width, height, 1.0]);

    this.selectedDayShader.setModelViewMatrix(gl, modelViewMatrix);
    this.selectedDayShader.draw(gl, camera);

    const endModelViewMatrix = mat4.create();
    mat4.translate(
      endModelViewMatrix, // destination matrix
      endModelViewMatrix, // matrix to translate
      [x, yStart, -1.0]
    ); // amount to translate
    mat4.scale(endModelViewMatrix, endModelViewMatrix, [endWidth, height, 1.0]);
    this.selectedDayShader.setModelViewMatrix(gl, endModelViewMatrix);

    this.selectedDayShader.draw(gl, camera);
  };

  public draw = (
    gl: WebGLRenderingContext,
    camera: Camera,
    timePos: number[],
    color: IRGBA,
    yRange?: [number, number]
  ): void => {
    const vertexCount = this.positions.length / 2.0;
    if (vertexCount === 0 || timePos.length !== 2) {
      return;
    }
    this.drawSelectedDay(gl, camera, timePos, color, yRange);
  };
}
